import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { Empty } from '@viquard/doc-ui/components';
import type {
  CalendarEvent,
  CalendarEventLink,
  CalendarGroup,
  CalendarKindMap,
} from '../types.js';
import {
  addMonths,
  buildMonthGrid,
  formatDayLong,
  formatMonth,
  isoDate,
  isSameDay,
  localDateKey,
  parseISODate,
  todayPlain,
  weekdayHeaders,
} from '../utils/dateUtils.js';
import { CalendarHeader } from '../components/CalendarHeader.js';
import { EventChip } from '../components/EventChip.js';
import { EventCard } from '../components/EventCard.js';
import { RailCard } from '../components/RailCard.js';
import { useCalendarKeyboard } from '../hooks/useCalendarKeyboard.js';
import type { EventMenuActions } from '../utils/eventMenu.js';
import { eventInstanceKey } from '../utils/identity.js';
import { formatMessage, useCalendarMessages } from '../i18n.js';

export interface MonthViewProps {
  readonly cursor: string;
  readonly onCursorChange: (next: string) => void;
  readonly selectedDate: string;
  readonly onSelectedDateChange: (next: string) => void;
  readonly today: string;
  readonly events: readonly CalendarEvent[];
  readonly kindTheme: CalendarKindMap;
  readonly groups?: readonly CalendarGroup[] | undefined;
  readonly locale?: string | undefined;
  readonly weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined;
  readonly viewerTz?: string | undefined;
  readonly onSelectEvent?: ((event: CalendarEvent) => void) | undefined;
  readonly onSelectLink?:
    | ((link: CalendarEventLink, event: CalendarEvent) => void)
    | undefined;
  /** Cap the number of event chips shown per cell before "+N more". Default 3. */
  readonly maxChipsPerCell?: number | undefined;
  /** Slot rendered below the day's event list in the rail. */
  readonly renderDayRailExtras?: ((dateKey: string) => ReactNode) | undefined;
  /** Slot for per-event extras (rendered inside each EventCard). */
  readonly renderEventExtras?: ((event: CalendarEvent) => ReactNode) | undefined;
  readonly contextMenu?: ((event: CalendarEvent) => EventMenuActions) | undefined;
  readonly selectedEventKey?: string | null | undefined;
}

const MAX_CHIPS_DEFAULT = 3;

export function MonthView(props: MonthViewProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    cursor,
    onCursorChange,
    selectedDate,
    onSelectedDateChange,
    today,
    events,
    kindTheme,
    groups,
    locale,
    weekStartsOn = 0,
    viewerTz,
    onSelectEvent,
    onSelectLink,
    maxChipsPerCell = MAX_CHIPS_DEFAULT,
    renderDayRailExtras,
    renderEventExtras,
    contextMenu,
    selectedEventKey,
  } = props;

  const cursorDate = useMemo(() => parseISODate(cursor), [cursor]);
  const todayDate = useMemo(() => parseISODate(today), [today]);
  const selectedPlain = useMemo(() => parseISODate(selectedDate), [selectedDate]);
  const grid = useMemo(() => buildMonthGrid(cursorDate, weekStartsOn), [cursorDate, weekStartsOn]);
  const headers = useMemo(
    () => ({
      short: weekdayHeaders(weekStartsOn, locale, 'short'),
      narrow: weekdayHeaders(weekStartsOn, locale, 'narrow'),
    }),
    [weekStartsOn, locale],
  );

  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach((e) => {
      const key = localDateKey(e.startsAt, viewerTz);
      const list = map.get(key);
      if (list === undefined) map.set(key, [e]);
      else list.push(e);
    });
    map.forEach((list) =>
      list.sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    );
    return map;
  }, [events, viewerTz]);

  const selectedEvents = eventsByDate.get(selectedDate) ?? [];

  // Roving-tabindex grid: keep a ref to each cell so we can focus the
  // newly-selected one after a keyboard arrow.
  const gridRef = useRef<HTMLDivElement>(null);
  const cellRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const wantFocusKeyRef = useRef<string | null>(null);

  const kbd = useCalendarKeyboard({
    selected: selectedDate,
    cursor,
    onSelectedChange: (next) => {
      onSelectedDateChange(next);
      wantFocusKeyRef.current = next;
    },
    onCursorChange,
    onActivate: (key) => {
      const list = eventsByDate.get(key);
      const first = list?.[0];
      if (first !== undefined && onSelectEvent !== undefined) {
        onSelectEvent(first);
      }
    },
    columns: 7,
    viewerTz,
  });

  // Roving-tabindex focus restore: after a keyboard arrow updates
  // `selectedDate`, focus the matching cell. Keyed off selectedDate so
  // the effect is explicit about when it runs.
  useEffect(() => {
    const want = wantFocusKeyRef.current;
    if (want === null) return;
    wantFocusKeyRef.current = null;
    const el = cellRefs.current.get(want);
    if (el !== undefined) el.focus();
  }, [selectedDate]);

  return (
    <div className="vq-cal__split vq-cal__split--month">
      <div className="vq-cal__panel vq-cal__panel--grid">
        <CalendarHeader
          label={formatMonth(cursorDate, locale)}
          onPrev={() => { onCursorChange(isoDate(addMonths(cursorDate, -1))); }}
          onNext={() => { onCursorChange(isoDate(addMonths(cursorDate, 1))); }}
          onToday={() => {
            const t = todayPlain(viewerTz);
            onCursorChange(isoDate(t));
            onSelectedDateChange(isoDate(t));
          }}
        />
        <div
          className="vq-cal__weekdays"
          role="row"
          aria-label="Day of week headers"
        >
          {headers.short.map((label, i) => (
            <div key={i} role="columnheader" className="vq-cal__weekday">
              <span className="vq-cal__weekday-long">{label}</span>
              <span className="vq-cal__weekday-narrow">{headers.narrow[i]}</span>
            </div>
          ))}
        </div>
        <div
          ref={gridRef}
          className="vq-cal__month-grid"
          role="grid"
          aria-label="Month grid"
          onKeyDown={kbd.onKeyDown}
        >
          {grid.map((cell) => {
            const key = isoDate(cell.date);
            const cellEvents = eventsByDate.get(key) ?? [];
            const isToday = isSameDay(cell.date, todayDate);
            const isSelected = key === selectedDate;
            const visible = cellEvents.slice(0, maxChipsPerCell);
            const overflow = cellEvents.length - visible.length;

            return (
              // Use a non-button element so that interactive EventChip
              // buttons inside don't violate "no nested button" — we
              // still expose grid semantics via role + tabIndex + click /
              // key handlers managed by the parent grid.
              <div
                key={key}
                role="gridcell"
                aria-selected={isSelected}
                aria-current={isToday ? 'date' : undefined}
                aria-disabled={cell.outOfMonth || undefined}
                tabIndex={isSelected ? 0 : -1}
                ref={(el) => {
                  if (el !== null) cellRefs.current.set(key, el);
                  else cellRefs.current.delete(key);
                }}
                onClick={() => {
                  if (!cell.outOfMonth) onSelectedDateChange(key);
                }}
                className={[
                  'vq-cal__cell',
                  cell.outOfMonth ? 'is-out' : '',
                  isToday ? 'is-today' : '',
                  isSelected ? 'is-selected' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="vq-cal__cell-head">
                  <span className="vq-cal__cell-date">{cell.date.day}</span>
                  {cellEvents.length > maxChipsPerCell && (
                    <span className="vq-cal__cell-count" aria-hidden="true">
                      {cellEvents.length}
                    </span>
                  )}
                </div>
                <div className="vq-cal__cell-events">
                  {visible.map((e) => (
                    <EventChip
                      key={eventInstanceKey(e)}
                      event={e}
                      kindTheme={kindTheme}
                      onClick={onSelectEvent}
                      viewerTz={viewerTz}
                      selected={selectedEventKey === eventInstanceKey(e)}
                      contextMenu={contextMenu?.(e)}
                    />
                  ))}
                  {overflow > 0 && (
                    <span className="vq-cal__cell-overflow">{formatMessage(messages.cellOverflow, { count: overflow })}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <aside className="vq-cal__rail" aria-label={messages.railAriaLabel}>
        <RailCard eyebrow={messages.railEyebrow} title={formatDayLong(selectedPlain, locale)}>
          {selectedEvents.length === 0 ? (
            <Empty
              size="compact"
              title={messages.emptyDayTitle}
              description={messages.emptyDayDescription}
            />
          ) : (
            <div className="vq-cal__card-stack">
              {selectedEvents.map((e) => (
                <EventCard
                  key={eventInstanceKey(e)}
                  event={e}
                  kindTheme={kindTheme}
                  groups={groups}
                  onClick={onSelectEvent}
                  onSelectLink={onSelectLink}
                  viewerTz={viewerTz}
                  extras={renderEventExtras?.(e)}
                  selected={selectedEventKey === eventInstanceKey(e)}
                  contextMenu={contextMenu?.(e)}
                />
              ))}
            </div>
          )}
        </RailCard>
        {renderDayRailExtras?.(selectedDate)}
      </aside>
    </div>
  );
}
